import { useEffect, useRef, useState } from "react";
import { StorageImage } from "./StorageImage";
import { Loader2, Upload } from "lucide-react";
import { Timestamp } from "firebase/firestore";

interface LazyStorageImageProps {
  storagePath: string;
  uploadStatus?: string;
  updatedAt?: Timestamp;
  alt?: string;
  className?: string;
}

/**
 * A wrapper around StorageImage that lazy loads the image only when it
 * becomes visible in the viewport. Uses IntersectionObserver with a 100px
 * root margin to pre-load images slightly before they scroll into view.
 *
 * Also handles uploadStatus to show appropriate states during upload.
 */
export const LazyStorageImage: React.FC<LazyStorageImageProps> = ({
  storagePath,
  uploadStatus,
  updatedAt,
  alt = "Image",
  className = "",
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Check if upload is complete (uploaded or no status means ready to display)
  const isUploaded = !uploadStatus || uploadStatus === "uploaded";

  return (
    <div ref={ref} className={className}>
      {!isUploaded ? (
        // Show uploading state while image is being uploaded
        <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center gap-1">
          <Upload className="h-4 w-4 text-gray-400 animate-pulse" />
          <span className="text-[10px] text-gray-400">Uploading...</span>
        </div>
      ) : isVisible ? (
        <StorageImage
          storagePath={storagePath}
          uploadStatus={uploadStatus}
          updatedAt={updatedAt}
          alt={alt}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  );
};
